/**
 * App Sound Service
 * 
 * Manages application-specific sound effects and background music,
 * building on top of the core AudioCache and MobileAudioService.
 */

import { Capacitor } from '@capacitor/core';
import audioCache from './audio-cache';
import { 
  playGameSound, 
  playBackgroundMusic, 
  pauseBackgroundMusic, 
  stopBackgroundMusic,
  setGameSoundsMuted,
  setGameSoundsVolume,
  setBackgroundMusicVolume,
  setBackgroundMusicMuted
} from '@/utils/sound-utils';

class AppSoundService {
  private isNative: boolean;
  public currentBackgroundMusic: string | null = null;
  private previousBackgroundMusic: string | null = null;
  private isMusicPaused: boolean = false;
  private isInGameRoom: boolean = false;

  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    
    // Load settings from localStorage
    this.loadSettings();
  }
  
  /**
   * Load sound settings from localStorage
   */
  private loadSettings(): void {
    const soundVolume = localStorage.getItem('sound-effects-volume');
    const soundMuted = localStorage.getItem('sound-effects-muted');
    const musicVolume = localStorage.getItem('background-music-volume');
    const musicMuted = localStorage.getItem('background-music-muted');
    
    if (soundVolume) setGameSoundsVolume(parseFloat(soundVolume));
    if (soundMuted) setGameSoundsMuted(soundMuted === 'true');
    if (musicVolume) setBackgroundMusicVolume(parseFloat(musicVolume));
    if (musicMuted) setBackgroundMusicMuted(musicMuted === 'true');
  }
  
  /**
   * Initialize background music when the app starts
   */
  public initializeAppSounds(): void {
    // Check if audio is muted in settings
    const soundMuted = localStorage.getItem('sound-effects-muted') === 'true';
    
    console.log(`Initializing app sounds (muted: ${soundMuted})`);
    
    // Apply mute settings to both sound effects and background music
    setGameSoundsMuted(soundMuted);
    setBackgroundMusicMuted(soundMuted);
    
    // Start with main menu music if sound is enabled
    if (!soundMuted) {
      this.playMenuBackgroundMusic();
    }
  }
  
  /**
   * Play the main menu background music
   */
  public playMenuBackgroundMusic(): void {
    if (this.currentBackgroundMusic === 'menu-bg-1') return;
    
    this.currentBackgroundMusic = 'menu-bg-1';
    playBackgroundMusic('menu-bg-1', { loop: false });
  }
  
  /**
   * Play game room background music
   * @param gameType Optional game type for specific game music
   */
  public playGameBackgroundMusic(gameType?: string): void {
    // Remember current music before switching
    if (this.currentBackgroundMusic && this.currentBackgroundMusic !== 'gameplay-bg-1') {
      this.previousBackgroundMusic = this.currentBackgroundMusic;
    }
    
    // For now, use a generic game background track
    // In the future, could use different tracks per game type
    this.currentBackgroundMusic = 'gameplay-bg-1';
    playBackgroundMusic('gameplay-bg-1', { loop: false });
  }
  
  /**
   * Pause the current background music (when entering a game)
   */
  public pauseBackgroundMusic(): void {
    if (!this.isMusicPaused) {
      pauseBackgroundMusic();
      this.isMusicPaused = true;
    }
  }
  
  /**
   * Resume the previously playing background music (when exiting a game)
   */
  public resumeBackgroundMusic(): void {
    if (this.isMusicPaused) {
      if (this.previousBackgroundMusic) {
        playBackgroundMusic(this.previousBackgroundMusic, { loop: false });
        this.currentBackgroundMusic = this.previousBackgroundMusic;
      } else {
        this.playMenuBackgroundMusic(); // Default fallback
      }
      this.isMusicPaused = false;
    }
  }
  
  /**
   * Stop all background music
   */
  public stopBackgroundMusic(): void {
    stopBackgroundMusic();
    this.currentBackgroundMusic = null;
    this.isMusicPaused = false;
  }
  
  /**
   * Play a UI interaction sound (buttons, toggles, etc.)
   * @param type The type of UI interaction
   */
  public playUISound(type: 'button' | 'toggle' | 'notification' | 'success' | 'error' = 'button'): void {
    playGameSound(`ui.${type}`);
  }
  
  /**
   * Play a menu navigation sound
   */
  public playNavSound(): void {
    playGameSound('ui.navigation');
  }
  
  /**
   * Play sound when clicking a button
   */
  public playButtonSound(): void {
    playGameSound('ui.button');
  }
  
  /**
   * Play daily reward sound when claiming a reward
   */
  public playDailyRewardSound(): void {
    playGameSound('win.reward');
  }
  
  /**
   * Play achievement reward claim sound
   */
  public playAchievementRewardSound(): void {
    playGameSound('win.achievement');
  }
  
  /**
   * Play game victory reward claim sound
   */
  public playGameVictoryRewardSound(): void {
    playGameSound('win.game');
  }
  
  /**
   * Play a purchase success sound
   */
  public playPurchaseSound(): void {
    playGameSound('ui.purchase');
  }
  
  /**
   * Play a game win sound
   * @param type Type of win
   */
  public playWinSound(type: 'game' | 'round' | 'tournament' = 'game'): void {
    playGameSound(`win.${type}`);
  }
  
  /**
   * Play a game lose sound
   * @param type Type of loss
   */
  public playLoseSound(type: 'game' | 'round' | 'tournament' = 'game'): void {
    playGameSound(`lose.${type}`);
  }
  
  /**
   * Play a notification sound
   */
  public playNotificationSound(): void {
    playGameSound('ui.notification');
  }

  /**
   * Called when entering a game room
   * Switches to game music and handles game-specific sound setup
   */
  public enterGameRoom(): void {
    this.isInGameRoom = true;
    
    // Switch from menu music to game music
    if (!this.isInGameRoom && this.currentBackgroundMusic !== 'gameplay-bg-1') {
      this.playGameBackgroundMusic();
    }
  }

  /**
   * Called when exiting a game room
   * Returns to previous/menu music
   */
  public exitGameRoom(): void {
    this.isInGameRoom = false;
    
    // Switch back to menu music or previous music
    if (this.previousBackgroundMusic) {
      playBackgroundMusic(this.previousBackgroundMusic, { loop: true });
      this.currentBackgroundMusic = this.previousBackgroundMusic;
    } else {
      this.playMenuBackgroundMusic();
    }
  }

  /**
   * Play card selected sound
   */
  public playCardSelectSound(): void {
    // Check if sound is muted before playing
    const soundMuted = localStorage.getItem('sound-effects-muted');
    if (soundMuted === 'true') return;
    
    playGameSound('gameplay.cardSelect');
  }

  /**
   * Play card played sound
   */
  public playCardPlayedSound(): void {
    // Check if sound is muted before playing
    const soundMuted = localStorage.getItem('sound-effects-muted');
    if (soundMuted === 'true') return;
    
    playGameSound('gameplay.cardPlace');
  }

  /**
   * Play deal cards sound
   */
  public playDealCardsSound(): void {
    // Check if sound is muted before playing
    const soundMuted = localStorage.getItem('sound-effects-muted');
    if (soundMuted === 'true') return;
    
    playGameSound('gameplay.cardDeal');
  }

  /**
   * Play round win sound
   */
  public playRoundWinSound(): void {
    // Check if sound is muted before playing
    const soundMuted = localStorage.getItem('sound-effects-muted');
    if (soundMuted === 'true') return;
    
    playGameSound('gameplay.roundEnd');
  }

  /**
   * Play game win sound
   */
  public playGameWinSound(): void {
    playGameSound('win.game');
  }

  /**
   * Play start new round sound
   */
  public playStartNewRoundSound(): void {
    playGameSound('ui.shuffle');
  }

  /**
   * Play replace cards sound (for Under 10 rule)
   */
  public playReplaceCardsSound(): void {
    playGameSound('gameplay.cardFlip');
  }

  /**
   * Play the app launch sound when the app starts
   */
  public playAppLaunchSound(): void {
    playGameSound('ui.success');
  }
}

// Create singleton instance
const appSound = new AppSoundService();

// Export for use in components
export default appSound;